<script lang="ts" setup>
  import {
    Checkbox,
    DROPDOWN_CONTEXT_KEY,
    DropdownApplyFooter,
    DropdownPanelScroll,
    dropdownPanelDividerClass,
    useSelectAll,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { computed, inject, ref, watch } from 'vue';
  import { useFilters } from '../../../../composables';
  import type {
    SelectFilterOptions,
    SingleSelectModelValue,
  } from '../../../../store';
  import { FilterTypes, getSelectOptionKey } from '../../../../utils';
  import {
    optionRowClass,
    selectMultipleFilterWrapperClass,
  } from './SelectMultipleFilter.css';

  const props = defineProps<{
    filterNamespace: string;
    filterLabel: string;
    filterFieldName: string;
    options: SelectFilterOptions;
    modelValue?: SingleSelectModelValue[];
  }>();

  defineOptions({
    inheritAttrs: false,
  });

  const { t } = useLocale('table');

  const dropdown = inject(DROPDOWN_CONTEXT_KEY, null);

  const currentFilterStore = useFilters(props.filterNamespace);

  const selectedItems = ref<SingleSelectModelValue[]>(props.modelValue || []);

  watch(
    () => props.modelValue,
    (value) => {
      selectedItems.value = value || [];
    },
  );

  const emit = defineEmits<{
    'update:modelValue': [SingleSelectModelValue[]];
  }>();

  const isSelected = (value: SingleSelectModelValue['value']): boolean =>
    selectedItems.value.some(
      (item: SingleSelectModelValue) =>
        getSelectOptionKey(item.value) === getSelectOptionKey(value),
    );

  const { allSelected, someSelected, selectableOptions } =
    useSelectAll<SingleSelectModelValue>({
      options: () => props.options,
      selected: () => selectedItems.value,
      isSelected: (option) => isSelected(option.value),
    });

  const toggleAllLabel = computed(() =>
    allSelected.value
      ? t('filters.select_multiple.deselect_all')
      : t('filters.select_multiple.select_all'),
  );

  const onToggleAll = () => {
    selectedItems.value = allSelected.value ? [] : [...selectableOptions.value];
  };

  const onApplyFilter = () => {
    emit('update:modelValue', selectedItems.value);
    currentFilterStore.applyFilterValue({
      filterFieldName: props.filterFieldName,
      value: selectedItems.value.length ? selectedItems.value : undefined,
      filterType: FilterTypes.SELECT_MULTIPLE,
    });
    dropdown?.close();
  };

  const onSelectItem = (item: SingleSelectModelValue) => {
    if (isSelected(item.value)) {
      selectedItems.value = selectedItems.value.filter(
        (i: SingleSelectModelValue) =>
          getSelectOptionKey(i.value) !== getSelectOptionKey(item.value),
      );
    } else {
      selectedItems.value = [...selectedItems.value, item];
    }
  };
</script>

<template>
  <DropdownPanelScroll
    :class="selectMultipleFilterWrapperClass"
    role="group"
    :aria-label="filterLabel"
  >
    <div :class="optionRowClass">
      <Checkbox
        :name="`${filterFieldName}-select-all`"
        :id="`${filterFieldName}-select-all`"
        :label="toggleAllLabel"
        :model-value="allSelected"
        :indeterminate="someSelected"
        full-width
        @update:model-value="onToggleAll"
      />
    </div>

    <div :class="dropdownPanelDividerClass" />

    <div
      v-for="option in options"
      :key="getSelectOptionKey(option.value)"
      :class="optionRowClass"
    >
      <Checkbox
        :name="`${filterFieldName}-${getSelectOptionKey(option.value)}`"
        :id="`${filterFieldName}-${getSelectOptionKey(option.value)}`"
        :label="option.label ?? getSelectOptionKey(option.value)"
        :model-value="isSelected(option.value)"
        full-width
        @update:model-value="() => onSelectItem(option)"
      />
    </div>
  </DropdownPanelScroll>
  <DropdownApplyFooter @apply="onApplyFilter" />
</template>
